<template>
  <v-dialog v-model="dialog" persistent max-width="500px">
    <v-card>
      <v-card-title class="text-h6">
        <v-icon left color="primary">mdi-clipboard-text</v-icon>
        Justificar Falta
      </v-card-title>

      <v-card-text>
        <div class="mb-4">
          <v-alert type="info" outlined dense class="mb-4">
            <div class="font-weight-medium">Aluno:</div>
            <div>{{ aluno?.nomecompleto || "Carregando..." }}</div>
          </v-alert>
        </div>

        <ValidationObserver ref="observer">
          <v-form @submit.prevent="confirmar">
            <ValidationProvider v-slot="{ errors }" name="justificativa" rules="required">
              <v-select
                id="justificarFalta.justificativa_id"
                v-model="form.justificativa_id"
                :items="justificativas"
                :item-value="(item) => item.id"
                :item-text="(item) => item.descricao"
                label="Selecione uma justificativa *"
                :error-messages="errors"
                outlined
                dense
                :loading="carregandoJustificativas"
                :disabled="salvando"
              >
                <template #item="{ item, on, attrs }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :id="`justificarFalta.justificativa_id-item-${item.id}`"
                  >
                    <v-list-item-title>{{ item.descricao }}</v-list-item-title>
                  </v-list-item>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title> Nenhuma justificativa encontrada </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </ValidationProvider>

            <v-file-input
              id="justificarFalta.file"
              v-model="form.file"
              label="Anexar arquivo (opcional)"
              outlined
              dense
              show-size
              accept=".pdf"
              prepend-icon="mdi-paperclip"
              :disabled="salvando"
              :rules="[rules.tamanhoMaximo, rules.apenasP]"
              hint="Apenas arquivos PDF são permitidos (máx. 4MB)"
              persistent-hint
            >
              <template v-slot:selection="{ text }">
                <v-chip small label color="primary">
                  <v-icon left small>mdi-file-pdf</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          id="justificarFalta.cancelar"
          text
          color="grey darken-1"
          @click="cancelar"
          :disabled="salvando"
        >
          <v-icon left small>mdi-close</v-icon>
          Cancelar
        </v-btn>

        <v-btn
          id="justificarFalta.confirmar"
          color="success"
          @click="confirmar"
          :loading="salvando"
          :disabled="!form.justificativa_id"
        >
          <v-icon left small>mdi-check</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "ModalJustificarFalta",

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    aula: {
      type: Object,
      required: true,
    },
    matricula: {
      type: Object,
      required: true,
    },
    justificativaId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      dialog: false,
      salvando: false,
      carregandoJustificativas: false,
      justificativas: [],
      form: {
        justificativa_id: null,
        file: null,
        matriculaSelect: null,
      },
      rules: {
        tamanhoMaximo: (file) =>
          !file || file.size <= 4 * 1024 * 1024 || "O arquivo deve ter no máximo 4MB",
        apenasP: (file) =>
          !file || file.type === "application/pdf" || "Apenas arquivos PDF são permitidos",
      },
    };
  },

  computed: {
    aluno() {
      return this.matricula?.aluno;
    },
  },

  watch: {
    value: {
      handler(newVal) {
        this.dialog = newVal;
        if (newVal) {
          this.inicializarModal();
        } else {
          this.resetForm();
        }
      },
      immediate: true,
    },

    dialog(newVal) {
      if (!newVal) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    async inicializarModal() {
      this.form = {
        justificativa_id: this.justificativaId ? parseInt(this.justificativaId, 10) : null,
        file: null,
        matriculaSelect: this.matricula,
      };

      if (!this.justificativas.length) {
        await this.carregarJustificativas();
      }
    },

    async carregarJustificativas() {
      this.carregandoJustificativas = true;
      try {
        const response = await this.$services.aulasService.justificativas();
        this.justificativas = response.justificativas || [];
      } catch (error) {
        this.$toast.danger("Erro ao carregar justificativas");
      } finally {
        this.carregandoJustificativas = false;
      }
    },

    async confirmar() {
      // Validar form
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.$toast.warn("Por favor, corrija os erros no formulário");
        return;
      }

      if (!this.form.justificativa_id) {
        this.$toast.warn("É necessário selecionar uma justificativa");
        return;
      }

      this.salvando = true;

      try {
        await this.$services.aulasService.justificarFalta(this.aula, this.form);
        this.$toast.success("Justificativa salva com sucesso!");
        this.$emit("sucesso", {
          matricula_id: this.matricula.id,
          justificativa_id: this.form.justificativa_id,
          hasDocument: !!this.form.file, // true se tem arquivo, false se não tem
        });

        this.fechar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },

    cancelar() {
      this.fechar();
    },

    fechar() {
      this.dialog = false;
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },

    resetForm() {
      this.form = {
        justificativa_id: null,
        file: null,
        matriculaSelect: null,
      };

      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }
    },
  },
};
</script>

<style scoped>
.v-card-title {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.v-alert {
  border-left: 4px solid #2196f3 !important;
}
</style>
